import React from 'react';
import { find } from 'linkifyjs';

// Match US phone numbers: (xxx) xxx-xxxx, xxx-xxx-xxxx, xxx.xxx.xxxx
const PHONE_REGEX = /(?:\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g;

interface TextSegment {
  type: 'url' | 'email' | 'phone';
  value: string;
  href: string;
  start: number;
  end: number;
}

function findMatches(text: string): TextSegment[] {
  const segments: TextSegment[] = [];

  // Use linkifyjs for URLs and emails (handles multi-dot domains, edge cases)
  const linkifyResults = find(text);
  for (const result of linkifyResults) {
    segments.push({
      type: result.type === 'email' ? 'email' : 'url',
      value: result.value,
      href: result.type === 'email'
        ? result.href
        : result.href.replace(/^http:\/\//, 'https://'),
      start: result.start,
      end: result.end,
    });
  }

  // Find phone numbers (skip if they overlap with linkify matches)
  let match: RegExpExecArray | null;
  const phoneRegex = new RegExp(PHONE_REGEX.source, 'g');
  while ((match = phoneRegex.exec(text)) !== null) {
    const start = match.index;
    const end = match.index + match[0].length;
    const overlaps = segments.some(s => start < s.end && end > s.start);
    if (!overlaps) {
      const digits = match[0].replace(/\D/g, '');
      segments.push({
        type: 'phone',
        value: match[0],
        href: `tel:+1${digits}`,
        start,
        end,
      });
    }
  }

  segments.sort((a, b) => a.start - b.start);
  return segments;
}

const linkClassName = "text-primary underline underline-offset-2 hover:text-primary/80";

/**
 * Takes a plain text string and returns React elements with
 * auto-linked URLs, emails, and phone numbers.
 * Uses linkifyjs for reliable URL/email detection.
 */
export function linkifyText(text: string): React.ReactNode[] {
  const matches = findMatches(text);
  if (matches.length === 0) return [text];

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  matches.forEach((seg, i) => {
    if (seg.start > lastIndex) {
      result.push(text.slice(lastIndex, seg.start));
    }

    if (seg.type === 'email') {
      result.push(
        <a key={`e-${i}`} href={seg.href} className={linkClassName}>
          {seg.value}
        </a>
      );
    } else if (seg.type === 'url') {
      result.push(
        <a key={`u-${i}`} href={seg.href} target="_blank" rel="noopener noreferrer" className={linkClassName}>
          {seg.value}
        </a>
      );
    } else if (seg.type === 'phone') {
      result.push(
        <a key={`p-${i}`} href={seg.href} className={linkClassName}>
          {seg.value}
        </a>
      );
    }

    lastIndex = seg.end;
  });

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

/**
 * Splits a long block of text into readable sentences.
 * Handles newlines and sentence boundaries (". " followed by uppercase).
 */
function splitIntoLines(text: string): string[] {
  const paragraphs = text.split('\n');
  const result: string[] = [];

  for (const para of paragraphs) {
    if (para.trim() === '') {
      result.push('');
      continue;
    }

    if (para.length < 120) {
      result.push(para);
      continue;
    }

    const parts = para.split(/\.\s+(?=[A-Z])/);
    if (parts.length > 1) {
      result.push(
        ...parts.map((s, i) => (i < parts.length - 1 ? s + '.' : s))
      );
    } else {
      result.push(para);
    }
  }

  return result;
}

/**
 * Renders a text block preserving newlines, splitting long text into
 * readable sentences, and auto-linking URLs/emails/phones.
 */
export function RichText({ text }: { text: string }) {
  const lines = splitIntoLines(text);
  const nonEmptyLines = lines.filter(l => l.trim() !== '');

  if (nonEmptyLines.length <= 1) {
    return (
      <p className="text-sm text-muted-foreground leading-relaxed">
        {linkifyText(nonEmptyLines[0] || '')}
      </p>
    );
  }

  return (
    <ul className="list-disc pl-4 space-y-1.5">
      {lines.map((line, i) => {
        if (line.trim() === '') {
          return null;
        }
        return (
          <li key={i} className="text-sm text-muted-foreground leading-relaxed">
            {linkifyText(line)}
          </li>
        );
      })}
    </ul>
  );
}
